<template>
  <div v-if="group && (group.hasRequirements || group.hasEarlyBirds)" :class="bgColor">
    <div class="flex gap-2 items-center">
      <svg :class="`w-5 h-5 flex-shrink-0 ${iconColor}`" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>

      <p class="justify-start text-secondary-900 text-sm font-semibold leading-normal ml-1">
        <template v-if="group.hasEarlyBirds && group.hasRequirements">
          Zapisy do tej grupy są dostępne dla uczestników poprzednich edycji:
          <span class="font-bold">{{ Object.values(earlyBirdNamesByIds).join(', ') }}</span>
          oraz posiadaczy
          <span class="font-bold">{{ group.requirementsFormatted }}</span>.
        </template>
        <template v-else-if="group.hasEarlyBirds">
          Zapisy do tej grupy są dostępne tylko dla uczestników poprzednich edycji:
          <span class="font-bold">{{ Object.values(earlyBirdNamesByIds).join(', ') }}</span>.
        </template>
        <template v-else-if="group.hasRequirements">
          Kurs jest dostępny wyłącznie dla posiadaczy
          <span class="font-bold">{{ group.requirementsFormatted }}</span>.
        </template>
      </p>
    </div>

    <!-- Not logged in: show login prompt + button -->
    <div v-if="!isUserLoggedIn" class="text-sm text-secondary-700 mt-1">
      <a :href="authorizationUrl" class="inline-flex items-center justify-center px-4 py-2 mt-1 bg-primary-500 text-neutral-950 font-semibold rounded-lg hover:bg-primary-600 transition-colors text-sm">
        Zaloguj się, aby sprawdzić czy możesz się zapisać
      </a>
    </div>
    <!-- Logged in and eligible -->
    <div v-else-if="isEligible" class="text-sm text-secondary-700 mt-1">
      Gratulacje! Spełniasz wymagania, aby zapisać się do tej grupy.
      <template v-if="group.enrollmentStart && group.enrollmentStart > new Date() && !earlyBirdEligible">
        <span v-if="group.hasEarlyBirds"><b>Zapisy Early Birds!</b> </span>
        Niestety musisz poczekać do rozpoczęcia zapisów {{ group.enrollmentStartFormatted }}.
      </template>
    </div>
    <!-- Logged in but not eligible -->
    <div v-else class="text-sm text-secondary-700 mt-1">
      <template v-if="group.hasEarlyBirds">
        Niestety widzimy, że nie skończyłeś odpowiedniego kursu, aby zapisać się do tej grupy. 
      </template>
      <template v-else-if="group.hasRequirements">
        Niestety nie kwalifikujesz się do tej grupy. 
      </template>
      <a href="/customer/courses" class="inline-flex items-center justify-center px-4 py-2 mt-1 bg-primary-500 text-neutral-950 font-semibold rounded-lg hover:bg-primary-600 transition-colors text-sm">
        Zobacz inne kursy dostępne w Akademii Improwizacji
      </a>
    </div>
  </div>
</template>

<script>
import Group from '@shared/model/Group.js';

export default {
  name: 'ReqBox',
  props: {
    group: { type: Object, required: true },
    user: { type: Object, required: false, default: null },
  },
  emits: ['eligible', 'eligibleEarlyBird'],

  data() {
    return {
      earlyBirdNamesByIds: {},
      earlyBirdEligible: false,
      // Bumped after initProfiles() so computed depending on user.profiles re-runs
      // even when the User class instance mutates in place (non-reactive mutation).
      profilesVersion: 0,
    };
  },

  async mounted() {
    console.log('[ReqBox] mounted', {
      group: this.group,
      hasEarlyBirds: this.group?.hasEarlyBirds,
      hasRequirements: this.group?.hasRequirements,
      user: this.user,
      isUserLoggedIn: this.isUserLoggedIn,
    });

    if (this.group.hasEarlyBirds) {
      this.earlyBirdNamesByIds = await Group.getGroupNamesByIds(this.group.earlyBirds);
      console.log('[ReqBox] earlyBirdNamesByIds resolved', this.earlyBirdNamesByIds);
    }

    await this.refreshEarlyBirdEligibility();
    await this.ensureUserProfiles();
  },

  methods: {
    async ensureUserProfiles() {
      console.log('[ReqBox] ensureUserProfiles called', {
        isUserLoggedIn: this.isUserLoggedIn,
        hasInitProfiles: typeof this.user?.initProfiles === 'function',
        hasIsInitialized: typeof this.user?.isInitialized === 'function',
        isInitialized: typeof this.user?.isInitialized === 'function' ? this.user.isInitialized() : 'n/a',
        currentDefaultProfile: typeof this.user?.defaultProfile === 'function' ? this.user.defaultProfile() : null,
      });

      if (!this.isUserLoggedIn) {
        console.log('[ReqBox] ensureUserProfiles: skip — not logged in');
        return;
      }
      if (typeof this.user.initProfiles !== 'function') {
        console.log('[ReqBox] ensureUserProfiles: skip — no initProfiles()');
        return;
      }

      // NOTE: We intentionally do NOT skip when isInitialized() === true.
      // `isInitialized()` only checks that `profiles !== undefined`, but cached/stale
      // profiles may be missing the `level` field (e.g. updateProfiles() deletes it
      // before persisting). Always re-fetch to guarantee level data is present.
      try {
        console.log('[ReqBox] ensureUserProfiles: calling initProfiles()...');
        await this.user.initProfiles();
        this.profilesVersion++; // trigger reactivity for computed defaultProfile/isEligible
        const profile = typeof this.user.defaultProfile === 'function' ? this.user.defaultProfile() : null;
        console.log('[ReqBox] ensureUserProfiles: initProfiles() done', {
          defaultProfile: profile,
          level: profile?.level,
          profilesVersion: this.profilesVersion,
        });
      } catch (e) {
        console.error('[ReqBox] Error initializing user profiles:', e);
      }
    },

    async refreshEarlyBirdEligibility() {
      const earlyBirdIds = this.group?.earlyBirds;
      const userId = this.user?.id;

      console.log('[ReqBox] refreshEarlyBirdEligibility', {
        hasEarlyBirds: this.group?.hasEarlyBirds,
        earlyBirdIds,
        userId,
      });

      if (!this.group?.hasEarlyBirds || !Array.isArray(earlyBirdIds) || earlyBirdIds.length === 0 || !userId) {
        console.log('[ReqBox] refreshEarlyBirdEligibility: skip — preconditions not met');
        this.earlyBirdEligible = false;
        return;
      }

      try {
        this.earlyBirdEligible = await Group.validateEarlyBirds(earlyBirdIds, userId);
        console.log('[ReqBox] refreshEarlyBirdEligibility: result', this.earlyBirdEligible);
      } catch (e) {
        this.earlyBirdEligible = false;
        console.error('[ReqBox] Error validating early birds:', e);
      }
    },
  },

  computed: {
    authorizationUrl() {
      return `/authorization?redirect=${encodeURIComponent(window.location.href)}`;
    },

    isUserLoggedIn() {
      if (!this.user) return false;
      const isEmptyPlainObject =
        this.user.constructor === Object && Object.keys(this.user).length === 0;
      return !isEmptyPlainObject;
    },

    iconColor() {
      if (!this.isUserLoggedIn) return 'text-gray-500';
      return this.isEligible ? 'text-green-500' : 'text-red-500';
    },

    bgColor() {
      const base = 'flex flex-col gap-2 px-3 py-2 rounded-lg my-4 border ';
      if (!this.isUserLoggedIn) return base + 'bg-secondary-100 border-gray-300';
      return base + (this.isEligible ? 'bg-green-100 border-green-500' : 'bg-red-100 border-red-500');
    },

    isEligible() {
      const g = this.group;
      if (!g) {
        console.log('[ReqBox] isEligible: no group → true');
        return true;
      }

      // Not logged in — eligibility is unknown
      if (!this.isUserLoggedIn) {
        console.log('[ReqBox] isEligible: not logged in → null');
        return null;
      }

      // EarlyBird eligibility bypasses requirements
      if (g.hasEarlyBirds && this.earlyBirdEligible) {
        console.log('[ReqBox] isEligible: earlyBird match → true');
        return true;
      }

      // No requirements path — only earlyBirds remain and user isn't eligible
      if (!g.hasRequirements) {
        console.log('[ReqBox] isEligible: no requirements & no earlyBird match → false');
        return false;
      }

      // Requirements check via user level
      const profile = this.defaultProfile;
      console.log('[ReqBox] isEligible: checking requirements', {
        profile,
        level: profile?.level,
        hasValidateRequirements: typeof g.validateRequirements === 'function',
      });
      if (profile == null) {
        console.log('[ReqBox] isEligible: profile is null → null');
        return null;
      }

      if (typeof g.validateRequirements === 'function') {
        try {
          const result = !!g.validateRequirements(profile.level);
          console.log('[ReqBox] isEligible: validateRequirements →', result);
          return result;
        } catch (e) {
          console.error('[ReqBox] Error validating requirements:', e);
          return false;
        }
      }

      return true;
    },

    defaultProfile() {
      // eslint-disable-next-line no-unused-expressions
      this.profilesVersion; // dependency to force re-eval after initProfiles()
      if (!this.isUserLoggedIn) return null;
      return typeof this.user.defaultProfile === 'function' ? this.user.defaultProfile() : null;
    },
  },

  watch: {
    group: {
      deep: true,
      async handler() {
        await this.refreshEarlyBirdEligibility();
      },
    },
    user: {
      deep: true,
      async handler(newVal, oldVal) {
        console.log('[ReqBox] watch:user fired', {
          newId: newVal?.id,
          oldId: oldVal?.id,
          isUserLoggedIn: this.isUserLoggedIn,
        });
        await this.ensureUserProfiles();
        await this.refreshEarlyBirdEligibility();
      },
    },
    earlyBirdEligible: {
      immediate: true,
      handler(val) {
        console.log('[ReqBox] watch:earlyBirdEligible →', val);
        this.$emit('eligibleEarlyBird', val);
      },
    },
    isEligible: {
      immediate: true,
      handler(val) {
        console.log('[ReqBox] watch:isEligible →', val);
        this.$emit('eligible', val);
      },
    },
  },
};
</script>

<style scoped>
/* kept minimal styling */
</style>